import { useMemo, useState } from 'react';
import { useCustomMutation } from '@refinedev/core';
import { useAllPages } from '@/hooks/use-all-pages';
import { Loader2, UserPlus, Tag as TagIcon, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { Badge } from '@/components/ui/badge';
import { UserSearchCombobox } from '@/components/shared';
import type { User as UserType } from '@/resources/users/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn, startOfDayUtcIso } from '@/lib/utils';
import { notify } from '@/lib/notify';
import { InvitationItemsBuilder, type InvitationItem } from './InvitationItemsBuilder';

interface CreateInvitationForUserDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

interface TagOption {
  id: number;
  name: string;
  color: string;
  deletedAt: string | null;
}

interface ErrorWithMessage {
  message: string;
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as ErrorWithMessage).message === 'string' &&
    (error as ErrorWithMessage).message.length > 0
  ) {
    return (error as ErrorWithMessage).message;
  }
  return fallback;
}

/**
 * Создание PENDING-приглашения для УЖЕ зарегистрированного пользователя.
 * Получатель выбирается из базы (UserSearchCombobox), приглашение создаётся
 * с привязкой по userId — контактные поля не заполняются. Доступы выдаются
 * позже отдельным действием (attach-user), статус остаётся PENDING.
 */
export const CreateInvitationForUserDialog = ({
  open,
  onClose,
  onCreated,
}: CreateInvitationForUserDialogProps) => {
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [items, setItems] = useState<InvitationItem[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const [accessEndsAt, setAccessEndsAt] = useState<Date | null>(null);

  const { data: allTags } = useAllPages<TagOption>({
    resource: 'tags',
    enabled: open,
    staleTime: 30_000,
  });
  const activeTags = useMemo<TagOption[]>(
    () => allTags.filter((t) => t.deletedAt === null),
    [allTags],
  );
  const tagsById = useMemo(() => {
    const m = new Map<number, TagOption>();
    for (const t of activeTags) { m.set(t.id, t); }
    return m;
  }, [activeTags]);

  const toggleTag = (id: number) => {
    setSelectedTagIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const createMutation = useCustomMutation();
  const isSubmitting = createMutation.mutation.isPending;

  const itemsInvalid = items.some((i) => i.targetId === null);

  const resetState = () => {
    setSelectedUser(null);
    setItems([]);
    setSelectedTagIds([]);
    setTagPickerOpen(false);
    setAccessEndsAt(null);
  };

  const handleClose = () => {
    if (isSubmitting) { return; }
    resetState();
    onClose();
  };

  const handleCreate = () => {
    if (!selectedUser) {
      notify.error('Выберите пользователя');
      return;
    }
    if (itemsInvalid) {
      notify.error('Заполните все строки доступа или удалите пустые');
      return;
    }

    createMutation.mutate(
      {
        url: '/user-invitations',
        method: 'post',
        values: {
          userId: selectedUser.id,
          tagIds: selectedTagIds.length > 0 ? selectedTagIds : null,
          items: items.length > 0 ? items : null,
          ...(accessEndsAt ? { accessEndsAt: startOfDayUtcIso(accessEndsAt) } : {}),
        },
      },
      {
        onSuccess: () => {
          notify.success('Приглашение создано (ожидает привязки доступов)');
          resetState();
          onCreated();
          onClose();
        },
        onError: (error) => {
          notify.error(getErrorMessage(error, 'Ошибка создания'));
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { handleClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Приглашение для существующего пользователя
          </DialogTitle>
          <DialogDescription>
            Создаёт приглашение в статусе «Ожидает» с привязкой к выбранному
            пользователю. Доступы выдаются позже привязкой приглашения.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <div className="space-y-2">
            <Label>Пользователь</Label>
            <UserSearchCombobox
              value={selectedUser}
              onChange={setSelectedUser}
              placeholder="Поиск по имени, email, телефону, телеграму…"
              disabled={isSubmitting}
            />
          </div>

          <InvitationItemsBuilder value={items} onChange={setItems} enabled={open} />

          <div className="space-y-2">
            <Label>Доступ действует до (опционально)</Label>
            <DatePicker
              value={accessEndsAt}
              onChange={setAccessEndsAt}
              disabled={isSubmitting}
              placeholder="Стандартный срок тарифа"
            />
            <p className="text-xs text-muted-foreground">
              Если задано — выдаваемая подписка закончится ровно в эту дату,
              независимо от длительности тарифа. Пусто — стандартный срок тарифа.
            </p>
          </div>

          <div className="rounded-lg border p-4 space-y-3">
            <div className="flex items-center gap-2">
              <TagIcon className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">Теги после привязки</Label>
            </div>
            <Popover open={tagPickerOpen} onOpenChange={setTagPickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" type="button" className="w-full justify-between font-normal">
                  <span className="flex flex-wrap items-center gap-1 min-w-0">
                    {selectedTagIds.length === 0 ? (
                      <span className="text-muted-foreground">Выберите теги (опционально)</span>
                    ) : (
                      selectedTagIds.map((id) => {
                        const t = tagsById.get(id);
                        if (!t) { return null; }
                        return (
                          <Badge
                            key={id}
                            variant="secondary"
                            className="gap-1"
                            style={{ backgroundColor: `${t.color}20`, color: t.color, borderColor: t.color }}
                          >
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: t.color }} />
                            {t.name}
                          </Badge>
                        );
                      })
                    )}
                  </span>
                  <TagIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" sideOffset={4}>
                <Command>
                  <CommandInput placeholder="Поиск тега..." />
                  <CommandList>
                    <CommandEmpty>Теги не найдены</CommandEmpty>
                    <CommandGroup>
                      {activeTags.map((tag) => {
                        const checked = selectedTagIds.includes(tag.id);
                        return (
                          <CommandItem
                            key={tag.id}
                            value={`${tag.name}-${tag.id}`}
                            onSelect={() => toggleTag(tag.id)}
                          >
                            <Check className={cn('mr-2 h-4 w-4', checked ? 'opacity-100' : 'opacity-0')} />
                            <span
                              className="h-3 w-3 rounded-full mr-2 flex-shrink-0"
                              style={{ backgroundColor: tag.color }}
                            />
                            {tag.name}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Отмена
          </Button>
          <Button
            onClick={handleCreate}
            disabled={isSubmitting || !selectedUser || itemsInvalid}
            title={itemsInvalid ? 'Заполните все строки доступа' : undefined}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <UserPlus className="h-4 w-4 mr-2" />
            )}
            Создать приглашение
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
